import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Descriptions, Empty, Tag } from 'antd';
import {
  ArrowDownOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CustomerServiceOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  PhoneOutlined,
  RobotOutlined,
  SwapOutlined,
  SyncOutlined,
  UserOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type { AiCallVo } from '@/facilities/modules/aiCall';
import {
  formatDuration,
  formatTs,
  isTransferred,
  takeoverInProgress,
  takeoverStatusLabel,
  transferLabel,
} from './utils';
import { TakeoverButton } from './Takeover';
import type { BrowserPhone } from './browserPhone';
import { LiveBadge } from './LiveBits';
import { isLive, useElapsed } from './liveUtils';

interface Props {
  call: AiCallVo | null;
  /** the operator pressed "Take over" and TMS accepted it */
  onTakeover?: (next: AiCallVo) => void;
  takeoverTarget?: 'phone' | 'browser';
  browserPhone?: BrowserPhone;
}

/** One look for every outcome: coloured borderless tag, icon, human label. */
function OutcomeTag({ call, live }: { call: AiCallVo; live: boolean }) {
  const { t } = useTranslation();
  if (live) {
    return (
      <Tag color="processing" bordered={false} icon={<SyncOutlined spin />}>
        {t('hotline_inProgress')}
      </Tag>
    );
  }
  if (isTransferred(call)) {
    return (
      <Tag color="purple" bordered={false} icon={<SwapOutlined />}>
        {t('hotline_transferred')}
      </Tag>
    );
  }
  switch (call.outcome) {
    case 'completed':
      return (
        <Tag color="success" bordered={false} icon={<CheckCircleOutlined />}>
          {t('hotline_outcome_completed')}
        </Tag>
      );
    case 'caller_hung_up':
      return (
        <Tag color="warning" bordered={false} icon={<PhoneOutlined />}>
          {t('hotline_outcome_callerHungUp')}
        </Tag>
      );
    case 'timeout':
      return (
        <Tag color="warning" bordered={false} icon={<ClockCircleOutlined />}>
          {t('hotline_outcome_timeout')}
        </Tag>
      );
    case 'error':
      return (
        <Tag color="error" bordered={false} icon={<ExclamationCircleOutlined />}>
          {t('hotline_outcome_error')}
        </Tag>
      );
    default:
      return (
        <Tag bordered={false} icon={<PhoneOutlined />}>
          {call.outcome || '-'}
        </Tag>
      );
  }
}

const NEAR_BOTTOM_PX = 48;

export default function CallTranscript({
  call,
  onTakeover,
  takeoverTarget,
  browserPhone,
}: Props) {
  const { t } = useTranslation();
  const live = isLive(call);
  const takingOver = takeoverInProgress(call);
  const dialogues = call?.dialogues || [];
  const elapsed = useElapsed(call?.startedAt || call?.createdAt, live);

  // the "transferred" line sits where the human leg starts (first officer / post-transfer line)
  const transferAt = (() => {
    if (!call || !isTransferred(call)) return null;
    const idx = dialogues.findIndex((d) => d.speaker === 'Officer');
    return idx >= 0 ? idx : null;
  })();
  const transferLine = call ? (
    <div className="live-transfer" data-testid="transfer-line">
      <SwapOutlined />
      <span>
        {transferLabel(call, t)}
        {call.transfer?.requestedAt
          ? ` · ${formatTs(call.transfer.requestedAt, 'HH:mm:ss')}`
          : ''}
      </span>
    </div>
  ) : null;

  const listRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  const seenRef = useRef<{ id: number | null; count: number }>({ id: null, count: 0 });
  const [unread, setUnread] = useState(0);
  const [enterFrom, setEnterFrom] = useState(Number.MAX_SAFE_INTEGER);

  const scrollToBottom = useCallback((smooth: boolean) => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    atBottomRef.current = true;
    setUnread(0);
  }, []);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    atBottomRef.current = distance < NEAR_BOTTOM_PX;
    if (atBottomRef.current) setUnread(0);
  };

  // new lines: follow them when the operator is at the bottom, otherwise count them
  useLayoutEffect(() => {
    const id = call?.id ?? null;
    const count = dialogues.length;
    const seen = seenRef.current;
    if (seen.id !== id) {
      seenRef.current = { id, count };
      setEnterFrom(Number.MAX_SAFE_INTEGER);
      setUnread(0);
      scrollToBottom(false);
      return;
    }
    if (count > seen.count) {
      const added = count - seen.count;
      seenRef.current = { id, count };
      setEnterFrom(live ? seen.count : Number.MAX_SAFE_INTEGER);
      if (atBottomRef.current) {
        scrollToBottom(true);
      } else {
        setUnread((n) => n + added);
      }
    } else if (count < seen.count) {
      seenRef.current = { id, count };
    }
  }, [call?.id, dialogues.length, live, scrollToBottom]);

  // the "listening" row changes the height while live: keep the bottom pinned
  useEffect(() => {
    if (live && atBottomRef.current) scrollToBottom(false);
  }, [live, scrollToBottom]);

  if (!call) {
    return (
      <div className="h-full flex items-center justify-center bg-[#fff] dark:bg-[#141414]">
        <Empty description={t('hotline_selectCall')} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#fff] dark:bg-[#141414] border-l border-[#E8E9ED] dark:border-[#ffffff21]">
      <div className="px-4 pt-4 pb-3 border-b border-[#E8E9ED] dark:border-[#ffffff21]">
        <div className="flex flex-wrap items-center justify-between mb-2 gap-2">
          <h2 className="text-[18px] font-semibold m-0 whitespace-nowrap">
            {t('callLog')}
          </h2>
          {live && (
            <div className="flex flex-wrap items-center justify-end gap-2 min-w-0">
              <LiveBadge since={call.startedAt || call.createdAt} />
              {onTakeover && (
                <TakeoverButton
                  call={call}
                  onRequested={onTakeover}
                  target={takeoverTarget}
                  phone={browserPhone}
                />
              )}
            </div>
          )}
        </div>
        <Descriptions
          size="small"
          column={1}
          colon={false}
          styles={{ label: { width: 110, color: '#00000073' } }}
          items={[
            {
              key: 'caller',
              label: t('hotline_caller'),
              children: call.callerNumber || '-',
            },
            { key: 'agent', label: t('hotline_agent'), children: call.agent },
            {
              key: 'started',
              label: t('hotline_started'),
              children: formatTs(call.startedAt || call.createdAt),
            },
            {
              key: 'duration',
              label: t('duration'),
              children: live ? (
                <span className="tabular-nums">{elapsed}</span>
              ) : (
                formatDuration(call.durationSeconds) || '-'
              ),
            },
            {
              key: 'outcome',
              label: t('hotline_outcome'),
              children: <OutcomeTag call={call} live={live} />,
            },
            {
              key: 'ref',
              label: t('hotline_bookingRef'),
              children: <span className="font-mono">{call.bookingReference || '-'}</span>,
            },
            {
              key: 'sid',
              label: 'Call SID',
              children: <span className="font-mono text-[12px]">{call.callSid}</span>,
            },
          ]}
        />
      </div>
      <div className="relative flex-1 min-h-0">
        <div
          ref={listRef}
          onScroll={onScroll}
          data-testid="transcript-list"
          className="h-full overflow-y-auto px-4 py-3 space-y-3 bg-[#F7F8FA] dark:bg-[#0f0f0f]"
        >
          {dialogues.length === 0 && !live && (
            <Empty description={t('hotline_noTranscript')} />
          )}
          {dialogues.map((d, i) => {
            const isAgent = d.speaker !== 'Caller';
            const isOfficer = d.speaker === 'Officer';
            const showTransferLine = !live && transferAt !== null && i === transferAt;
            return (
              <Fragment key={i}>
                {showTransferLine && transferLine}
                <div
                  className={clsx(
                    'flex gap-2',
                    isAgent ? 'justify-start' : 'justify-end',
                    i >= enterFrom && 'msg-enter',
                  )}
                >
                  {isAgent && (
                    <div
                      className={clsx(
                        'shrink-0 w-8 h-8 rounded-full text-white flex items-center justify-center',
                        isOfficer ? 'bg-[#722ed1]' : 'bg-[#0364FF]',
                      )}
                    >
                      {isOfficer ? <CustomerServiceOutlined /> : <RobotOutlined />}
                    </div>
                  )}
                  <div
                    className={clsx(
                      'max-w-[80%] rounded-2xl px-3 py-2 shadow-sm text-[14px] leading-5',
                      isOfficer
                        ? 'bg-[#f9f0ff] dark:bg-[#2a1a3d] rounded-tl-sm border border-[#efdbff] dark:border-[#3e2a5a]'
                        : isAgent
                          ? 'bg-[#fff] dark:bg-[#1f1f1f] rounded-tl-sm'
                          : 'bg-[#0364FF] text-white rounded-tr-sm',
                    )}
                  >
                    <p
                      className={clsx(
                        'text-[11px] mb-0.5',
                        isAgent
                          ? 'text-[#00000073] dark:text-[#ffffff73]'
                          : 'text-[#ffffffb3]',
                      )}
                    >
                      {d.speaker} · {formatTs(d.timestamp, 'HH:mm:ss')}
                    </p>
                    <p className="whitespace-pre-wrap break-words">{d.message}</p>
                  </div>
                  {!isAgent && (
                    <div className="shrink-0 w-8 h-8 rounded-full bg-[#E8E9ED] dark:bg-[#333] flex items-center justify-center">
                      <UserOutlined />
                    </div>
                  )}
                </div>
              </Fragment>
            );
          })}
          {!live && isTransferred(call) && transferAt === null && transferLine}
          {!live && call.transfer?.status === 'expired' && transferLine}
          {takingOver && (
            <div className="live-transfer" data-testid="takeover-line">
              <LoadingOutlined spin />
              <span>
                {call.transfer?.requestedByName
                  ? `${t('hotline_takeoverBy', { name: call.transfer.requestedByName })} · `
                  : ''}
                {takeoverStatusLabel(call, t)}
                {call.transfer?.takeoverRequestedAt
                  ? ` · ${formatTs(call.transfer.takeoverRequestedAt, 'HH:mm:ss')}`
                  : ''}
              </span>
            </div>
          )}
          {live && !takingOver && (
            <div className="live-listening" data-testid="live-listening">
              <span className="live-listening__dots">
                <i />
                <i />
                <i />
              </span>
              {dialogues.length === 0 ? t('hotline_connecting') : t('hotline_listening')}
            </div>
          )}
        </div>
        {unread > 0 && (
          <button
            type="button"
            className="live-jump"
            onClick={() => scrollToBottom(true)}
            data-testid="transcript-jump"
          >
            <ArrowDownOutlined />
            {unread} · {t('hotline_newMessages')}
          </button>
        )}
      </div>
    </div>
  );
}
